/* Client-view preview overlay — reference DOM (.cv-*), real data.
 * Resolves the workspace's first shared collection and renders it exactly as an
 * anonymous client would see the public /l/:token lookbook: real renders, real
 * approve / request-changes / comment (publicApprove/publicComment) gated by the
 * collection's access level. If no collection is shared yet, an honest empty
 * state (never a faked lookbook). */
import { useEffect, useMemo, useState, type MouseEvent } from "react";
import QRCode from "qrcode";
import { useApp } from "./ctx";
import { useToast, promptDialog } from "@/shared/ui";
import { useAuth } from "@/features/auth/context/AuthContext";
import {
  listCollections,
  resolveLookbook,
  type LookbookItem,
  type ResolvedLookbook,
} from "@/features/collections/collectionsApi";
import { publicApprove, publicComment, publicListComments, type Comment } from "@/features/approvals/approvalsApi";
import { track } from "@/features/insights/insightsApi";

const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c] ?? c));
const brandSvg = (name: string) =>
  `<circle cx="17" cy="17" r="13" fill="none" stroke="#211F1B" stroke-width="2"/><path d="M17 8l4 6-4 6-4-6z" fill="#A07E3E"/><text x="38" y="22" font-family="Inter,-apple-system,Segoe UI,sans-serif" font-size="15" font-weight="600" fill="#211F1B" letter-spacing="1">${esc(name.toUpperCase())}</text>`;

const pillFor = (item: LookbookItem): string => {
  const ext = item.name.split(".").pop()?.toLowerCase();
  if (ext && ext !== item.name.toLowerCase() && ext.length <= 5) return ext.toUpperCase();
  return "RENDER";
};
const ACCESS_NOTE: Record<string, string> = {
  view: "View only", download: "View & download", comment: "View & comment", approve: "Review & approve",
};
const pinPct = (v: number) => `${Math.min(96, Math.max(4, v <= 1 ? v * 100 : v))}%`;

type UiState = "loading" | "none" | "ready";
type Decision = "approved" | "changes";

export default function ClientView() {
  const { setClient, go } = useApp();
  const { toast } = useToast();
  const { activeWorkspace } = useAuth();
  const studioName = activeWorkspace?.workspaceName || "the studio";

  const [state, setState] = useState<UiState>("loading");
  const [book, setBook] = useState<ResolvedLookbook | null>(null);
  const [token, setToken] = useState("");
  const [comments, setComments] = useState<Comment[]>([]);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [approving, setApproving] = useState(false);
  const [qrUrl, setQrUrl] = useState("");

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const cols = await listCollections();
        const shared = cols.find((c) => c.shareEnabled && c.shareToken);
        if (!shared) { if (live) setState("none"); return; }
        const resolved = await resolveLookbook(shared.shareToken);
        if (!live) return;
        if (!resolved) { setState("none"); return; }
        setBook(resolved);
        setToken(shared.shareToken);
        setState("ready");
        publicListComments(shared.shareToken).then((cs) => live && setComments(cs)).catch(() => {});
      } catch {
        if (live) setState("none");
      }
    })();
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!token) return;
    let live = true;
    QRCode.toDataURL(`${window.location.origin}/l/${token}`, { margin: 1, width: 200 })
      .then((u) => live && setQrUrl(u))
      .catch(() => {});
    return () => { live = false; };
  }, [token]);

  // Session-local pins (public comment endpoint stores no targetFileId — same
  // caveat as the real /l/:token page; loaded pins can't be re-placed on a card).
  const pinsByItem = useMemo(() => {
    const m = new Map<string, Comment[]>();
    for (const c of comments) {
      if (!c.pinned || !c.pin || !c.targetFileId) continue;
      const list = m.get(String(c.targetFileId)) ?? [];
      list.push(c);
      m.set(String(c.targetFileId), list);
    }
    return m;
  }, [comments]);

  if (state === "loading") {
    return (
      <div id="clientView" className="open">
        <div className="cv-top">
          <span style={{ flex: 1 }} />
          <span className="hint">Loading preview…</span>
        </div>
        <button className="cv-exit" onClick={() => setClient(false)}>Exit client-view preview</button>
      </div>
    );
  }

  if (state === "none" || !book) {
    return (
      <div id="clientView" className="open">
        <div className="cv-top">
          <svg width="138" height="30" viewBox="0 0 178 34" dangerouslySetInnerHTML={{ __html: brandSvg(studioName) }} />
          <span style={{ flex: 1 }} />
        </div>
        <div className="cv-hero">
          <h1>Nothing shared yet</h1>
          <div className="hint" style={{ marginTop: 5 }}>
            Create a collection and turn on its share link — this is exactly what your clients will see.
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button className="btn gold" onClick={() => { setClient(false); go("collections"); }}>Go to Collections</button>
          </div>
        </div>
        <button className="cv-exit" onClick={() => setClient(false)}>Exit client-view preview</button>
      </div>
    );
  }

  const { name, intro, clientName, access, items } = book;
  const canApprove = access === "approve";
  const canComment = access === "comment" || access === "approve";
  const canDownload = access === "download";
  const hasRenders = items.some((i) => i.posterUrl);

  const submitDecision = async (d: Decision) => {
    if (!token) return;
    setApproving(true);
    try {
      await publicApprove(token, { decision: d, ...(clientName ? { clientName } : {}) });
      setDecision(d);
    } catch {
      toast({ tone: "error", title: "Could not submit", description: "Please try again in a moment." });
    } finally {
      setApproving(false);
    }
  };

  const commentOn = async (item: LookbookItem, e: MouseEvent<HTMLDivElement>) => {
    if (!canComment || !token) return;
    const r = e.currentTarget.getBoundingClientRect();
    const pin = { x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 };
    const body = (await promptDialog({ title: `Comment on ${item.name}`, label: "Comment" }))?.trim();
    if (!body) return;
    try {
      const c = await publicComment(token, { authorName: clientName || "Client", body, pin });
      setComments((cs) => [...cs, { ...c, targetFileId: item.id, pin, pinned: true }]);
      toast({ tone: "success", title: "Comment sent", description: `${studioName} will see your note.` });
    } catch {
      toast({ tone: "error", title: "Could not send comment", description: "Please try again." });
    }
  };

  const downloadAll = () => {
    if (!token) return;
    track(token, "download", undefined, { all: true });
    for (const it of items) {
      if (!it.posterUrl) continue;
      const a = document.createElement("a");
      a.href = it.posterUrl;
      a.download = it.name || "render";
      a.target = "_blank";
      a.rel = "noopener";
      a.click();
    }
  };

  const saveOne = (item: LookbookItem) => {
    if (!token || !item.posterUrl) return;
    track(token, "download", item.id);
    window.open(item.posterUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div id="clientView" className="open">
      <div className="cv-top">
        <svg width="138" height="30" viewBox="0 0 178 34" dangerouslySetInnerHTML={{ __html: brandSvg(studioName) }} />
        <span style={{ flex: 1 }} />
        <span className="hint">Shared with you · {ACCESS_NOTE[access]}</span>
        {hasRenders && canDownload && <button className="btn gold" onClick={downloadAll}>Download all renders</button>}
      </div>

      <div className="cv-hero">
        <h1>{name}</h1>
        <div className="hint" style={{ marginTop: 5 }}>
          {items.length} {items.length === 1 ? "piece" : "pieces"} · shared by {studioName}{" "}
          <span className="badge gold">Private link</span>
          {intro ? <> · {intro}</> : null}
        </div>
        {(canApprove || canComment) && (
          <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
            {canApprove && (
              <button className="btn gold" disabled={approving || !!decision} style={decision ? { opacity: .75 } : undefined} onClick={() => submitDecision("approved")}>
                {decision === "approved" ? "Approved — the studio has been notified" : decision === "changes" ? "Changes requested" : "Approve collection"}
              </button>
            )}
            {canApprove && !decision && (
              <button className="btn ghost" disabled={approving} onClick={() => submitDecision("changes")}>Request changes</button>
            )}
            {canComment && <span className="hint">Click any image to pin a comment — your feedback goes straight to {studioName}.</span>}
          </div>
        )}
      </div>

      <div className="cv-grid">
        {items.map((item) => {
          const pins = pinsByItem.get(item.id) ?? [];
          return (
            <div key={item.id} className="file-card" style={{ cursor: canComment ? "crosshair" : "default" }}>
              <div className="thumb" onClick={(e) => commentOn(item, e)}>
                {item.posterUrl
                  ? <img src={item.posterUrl} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <div style={{ width: "100%", height: "100%", background: "linear-gradient(150deg,#FBF7EE,#EFE7D5)" }} />}
                <span className="type-pill">{pillFor(item)}</span>
                {pins.map((c, i) => (
                  <span key={c._id} className="pin" style={{ left: pinPct(c.pin!.x), top: pinPct(c.pin!.y) }} title={`${c.authorName ?? "Client"}: ${c.body}`}>{i + 1}</span>
                ))}
              </div>
              <div className="file-info">
                <span className="mini-ico" style={{ background: "#7A5C2E" }}>IMG</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="fi-name">{item.name}</div>
                  <div className="fi-meta">{canDownload ? <button className="btn ghost" style={{ padding: "2px 8px", fontSize: 11 }} onClick={(e) => { e.stopPropagation(); saveOne(item); }}>Save</button> : "Shared render"}</div>
                </div>
              </div>
            </div>
          );
        })}

        {qrUrl && (
          <div className="file-card" style={{ cursor: "default" }}>
            <div className="thumb" style={{ background: "linear-gradient(150deg,#FBF7EE,#EFE7D5)" }}>
              <img src={qrUrl} alt="Scan to open on your phone" width={76} height={76} style={{ borderRadius: 6, background: "#fff", padding: 4 }} />
            </div>
            <div className="file-info">
              <span className="mini-ico" style={{ background: "#A0793D" }}>AR</span>
              <div><div className="fi-name">Try it on — scan with your phone</div><div className="fi-meta">Opens this lookbook on any device</div></div>
            </div>
          </div>
        )}
      </div>

      <div style={{ textAlign: "center", color: "var(--muted)", fontSize: 12, paddingBottom: 70 }}>Powered by Vault</div>
      <button className="cv-exit" onClick={() => setClient(false)}>Exit client-view preview</button>
    </div>
  );
}
